<template>
  <div class="page-container">
    <!-- 统计卡片 -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-header"><span class="stat-label">待审核</span><div class="stat-icon amber">⏳</div></div>
        <div class="stat-value">{{ stats.pending || 0 }}</div>
        <div class="stat-change"><span class="change-text">需及时处理</span></div>
      </div>
      <div class="stat-card">
        <div class="stat-header"><span class="stat-label">已通过</span><div class="stat-icon green">✓</div></div>
        <div class="stat-value">{{ stats.passed || 0 }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-header"><span class="stat-label">总审核数</span><div class="stat-icon blue">📋</div></div>
        <div class="stat-value">{{ stats.total || 0 }}</div>
      </div>
    </div>

    <!-- 搜索 -->
    <div class="filter-bar">
      <el-form :inline="true" :model="search" size="small">
        <el-form-item label="订单号"><el-input v-model="search.orderNo" placeholder="订单号" clearable style="width:140px;"></el-input></el-form-item>
        <el-form-item label="用户ID"><el-input v-model="search.customerNo" placeholder="用户ID" clearable style="width:130px;"></el-input></el-form-item>
        <el-form-item label="姓名"><el-input v-model="search.name" placeholder="姓名" clearable style="width:120px;"></el-input></el-form-item>
        <el-form-item label="国家">
          <el-select v-model="search.countryCode" placeholder="全部" clearable style="width:130px;">
            <el-option v-for="c in countries" :key="c.countryCode" :label="(c.countryCname||c.countryName) + ' ' + c.countryCode" :value="c.countryCode"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="认证类型">
          <el-select v-model="search.authType" placeholder="全部" clearable style="width:130px;">
            <el-option label="证件信息" :value="1"></el-option>
            <el-option label="银行卡信息" :value="2"></el-option>
            <el-option label="电子钱包信息" :value="3"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="审核状态">
          <el-select v-model="search.status" placeholder="全部" clearable style="width:100px;">
            <el-option label="待审核" :value="1"></el-option>
            <el-option label="已通过" :value="2"></el-option>
            <el-option label="已拒绝" :value="3"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="提交时间">
          <el-date-picker v-model="search.submitDate" type="daterange" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd" style="width:260px;"></el-date-picker>
        </el-form-item>
        <el-form-item><el-button type="primary" @click="handleSearch">搜索</el-button><el-button @click="resetSearch">重置</el-button></el-form-item>
      </el-form>
    </div>

    <!-- Tabs -->
    <div class="tabs-bar">
      <button :class="['tab-btn', {active: activeTab==='all'}]" @click="switchTab('all')">全部 ({{ stats.total || 0 }})</button>
      <button :class="['tab-btn', {active: activeTab==='pending'}]" @click="switchTab('pending')">待审核 ({{ stats.pending || 0 }})</button>
      <button :class="['tab-btn', {active: activeTab==='passed'}]" @click="switchTab('passed')">已通过 ({{ stats.passed || 0 }})</button>
      <button :class="['tab-btn', {active: activeTab==='rejected'}]" @click="switchTab('rejected')">已拒绝 ({{ stats.rejected || 0 }})</button>
    </div>

    <!-- 表格 -->
    <div class="table-wrapper">
      <div class="table-header">
        <span class="table-title">{{ tabTitle }}</span>
        <div class="table-actions">
          <button v-auth="'business:customerAuditRecord:review'" class="btn btn-primary btn-sm" @click="batchApprove">✓ 审核通过</button>
          <button v-auth="'business:customerAuditRecord:review'" class="btn btn-danger btn-sm" @click="openBatchReject">✕ 拒绝</button>
          <button v-auth="'business:customerAuditRecord:export'" class="btn btn-secondary btn-sm" @click="handleExport">📥 导出</button>
        </div>
      </div>
      <el-table ref="auditTable" :data="tableData" border v-loading="loading" style="width:100%;" @selection-change="handleSelectionChange">
        <!-- 选择列宽度必须 ≥40：body 侧的 .cell 比 header 多 4px 右内边距，35px 时复选框那行会
             溢出 13px，被 Element 的 .el-table .cell{overflow:hidden;text-overflow:ellipsis}
             画成复选框右边一串「…」（表头不溢出所以只有数据行有）。40 是实测不溢出的最小宽度，
             也是仓库里其它页选择列的常用宽度；align=center 让复选框居中 -->
        <el-table-column type="selection" width="40" align="center"></el-table-column>
        <el-table-column prop="orderNo" label="订单号" width="160"></el-table-column>
        <el-table-column prop="customerNo" label="用户ID" width="120"></el-table-column>
        <el-table-column prop="name" label="姓名" width="90"></el-table-column>
        <el-table-column label="国家" width="100">
          <template slot-scope="{row}">{{ countryLabel(row.countryCode) }}</template>
        </el-table-column>
        <el-table-column label="认证类型" width="110">
          <template slot-scope="{row}">{{ authTypeLabel(row.authType) }}</template>
        </el-table-column>
        <el-table-column prop="createDate" label="提交时间" width="160"></el-table-column>
        <el-table-column label="审核状态" width="90">
          <template slot-scope="{row}">
            <el-tag :type="statusType(row.status)" size="mini">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="reviewTime" label="审核时间" width="160"></el-table-column>
        <el-table-column prop="reviewer" label="审核人" width="100"></el-table-column>
        <!-- 审核备注：通过/拒绝都会写在同一列（t_customer_audit_record.reject_reason） -->
        <el-table-column prop="rejectReason" label="备注" width="150" show-overflow-tooltip></el-table-column>
        <!-- 操作列按状态换文案：待审核 →「审核」，已通过/已拒绝 →「详情」，两者进的是同一个弹窗，
             弹窗底部按 status 切换：待审核给可编辑的「审核结论」区，已审结给只读的「审核结果」回显。
             这里故意不加 v-auth：原先的「详情」就是不鉴权的，加上会把仅有查看权限的人挡在资料之外；
             弹窗内的审核结论区与提交按钮各自带权限指令 -->
        <el-table-column label="操作" width="90" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" style="color:var(--brand-primary);" @click="openReview(scope.row)">{{ scope.row.status === 1 ? '审核' : '详情' }}</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination @size-change="(v)=>{pageSize=v;fetchList()}" @current-change="(v)=>{pageNum=v;fetchList()}"
        :current-page="pageNum" :page-sizes="[15,30,50]" :page-size="pageSize" layout="total,sizes,prev,pager,next,jumper" :total="total"></el-pagination>
    </div>

    <!-- 审核弹窗：上半部分是资料明细（用户信息 + 变更对比表），下半部分是审核结论区（仅待审核记录出现）。
         通过/拒绝收在一个弹窗里，拒绝时在下方追加「拒绝原因」；独立的拒绝弹窗已合并进来 -->
    <el-dialog :visible.sync="reviewVisible" width="800px" :close-on-click-modal="false" v-if="detail">
      <div slot="title"><h3 style="margin:0;">{{ detail.status === 1 ? '资料审核' : '资料审核详情' }}</h3></div>
      <!-- 资料明细分两块，各带一个分区标题：上半「用户信息」，下半是快照对比表（标题取快照带的实体名） -->
      <div class="detail-section-title">用户信息</div>
      <div class="detail-grid">
        <div class="detail-item"><div class="di-label">订单号</div><div class="di-value mono">{{ detail.orderNo }}</div></div>
        <div class="detail-item"><div class="di-label">用户ID</div><div class="di-value mono">{{ detail.customerNo }}</div></div>
        <div class="detail-item"><div class="di-label">姓名</div><div class="di-value">{{ detail.name }}</div></div>
        <div class="detail-item"><div class="di-label">国家/地区</div><div class="di-value">{{ countryLabel(detail.countryCode) }}</div></div>
        <div class="detail-item"><div class="di-label">客户组</div><div class="di-value">{{ detail.groupName || '--' }}</div></div>
        <div class="detail-item"><div class="di-label">认证类型</div><div class="di-value">{{ authTypeLabel(detail.authType) }}</div></div>
        <!-- 审核状态/审核人/审核时间/拒绝原因/审核备注这些「审核结论」相关字段一律不在这里列：
             待审核时它们必然空着（全是 --），已审结时由弹窗底部的「审核结果」块统一回显 -->
        <div class="detail-item"><div class="di-label">提交时间</div><div class="di-value">{{ detail.createDate }}</div></div>
      </div>
      <div v-if="detailSnapshots.length" style="margin-top:16px;">
        <div v-for="(snap, si) in detailSnapshots" :key="si" style="margin-bottom:16px;">
          <!-- 分区标题直接用小写实体名（证件信息/银行卡信息/电子钱包信息…，随记录类型变），
               前面不再挂「变更详情 — 」前缀，与上面的「用户信息」标题同款 -->
          <div class="detail-section-title">{{ snap.entityType || '资料' }}</div>
          <table class="diff-table">
            <thead><tr><th style="width:120px;">字段</th><th>修改前</th><th style="width:36px;"></th><th>修改后</th></tr></thead>
            <tbody>
              <tr v-for="(ch, ci) in snap.changes" :key="ci">
                <td class="diff-field">{{ ch.label }}</td>
                <!-- 证件正/反面、手持证件、银行卡证明这些字段存的是图片地址：
                     直接渲染线上图片并支持点击放大，不再把 URL 当文本摊在单元格里 -->
                <td class="diff-old">
                  <el-image v-if="isImageChange(ch) && ch.old" :src="ch.old" :preview-src-list="[ch.old]" fit="cover" class="diff-img">
                    <div slot="error" class="diff-img-err">加载失败</div>
                  </el-image>
                  <span v-else-if="ch.old">{{ ch.old }}</span>
                  <span v-else class="diff-empty">（空）</span>
                </td>
                <td class="diff-arrow">→</td>
                <td class="diff-new">
                  <el-image v-if="isImageChange(ch) && ch.new" :src="ch.new" :preview-src-list="[ch.new]" fit="cover" class="diff-img">
                    <div slot="error" class="diff-img-err">加载失败</div>
                  </el-image>
                  <span v-else-if="ch.new">{{ ch.new }}</span>
                  <span v-else class="diff-empty">（空）</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 审核结论区：仅待审核记录出现（v-auth 用 visibility 隐藏，无权限的人看不到也点不到） -->
      <div v-if="detail.status === 1" v-auth="'business:customerAuditRecord:review'" class="review-block">
        <div class="review-block-title">审核结论</div>
        <el-radio-group v-model="reviewForm.conclusion" class="review-radio">
          <el-radio :label="1">通过</el-radio>
          <el-radio :label="2">拒绝</el-radio>
        </el-radio-group>
        <div class="review-field">审核备注</div>
        <el-input type="textarea" v-model="reviewForm.remark" :rows="4" resize="vertical" placeholder="请输入审核备注（选填）"></el-input>
        <template v-if="reviewForm.conclusion === 2">
          <div class="review-field" style="margin-top:12px;">拒绝原因 <span class="review-required">*</span></div>
          <el-input type="textarea" v-model="reviewForm.reason" :rows="3" resize="vertical" placeholder="请输入拒绝原因"></el-input>
        </template>
      </div>

      <!-- 审核结果：已通过/已拒绝的记录（即「详情」入口）在弹窗底部只读回显审核结论。
           状态用彩色胶囊；拒绝原因整块只在已拒绝时出现（通过的单子本来就没有原因，空框反而怪）。
           审核备注是审核员通过/拒绝时填的原话，两种结论都可能填，故不分状态、整块都出现 -->
      <div v-if="detail.status !== 1" class="result-block">
        <div class="result-title">审核结果</div>
        <div class="result-grid">
          <div class="result-cell">
            <div class="result-label">审核状态</div>
            <span :class="['result-pill', detail.status === 2 ? 'pass' : 'reject']">{{ statusLabel(detail.status) }}</span>
          </div>
          <div class="result-cell">
            <div class="result-label">审核人</div>
            <div class="result-value">{{ detail.reviewer || '--' }}</div>
          </div>
          <div class="result-cell">
            <div class="result-label">审核时间</div>
            <div class="result-value">{{ detail.reviewTime || '--' }}</div>
          </div>
        </div>
        <div class="result-note">
          <div class="result-note-label">审核备注</div>
          <div class="result-note-box">{{ detail.reviewRemark || '--' }}</div>
        </div>
        <div v-if="detail.status === 3" class="result-reason">
          <div class="result-reason-label">拒绝原因</div>
          <div class="result-reason-box">{{ detail.rejectReason || '--' }}</div>
        </div>
      </div>

      <div slot="footer">
        <el-button @click="reviewVisible=false">{{ detail.status === 1 ? '取消' : '关闭' }}</el-button>
        <el-button v-if="detail.status === 1" v-auth="'business:customerAuditRecord:review'" type="primary"
          :loading="reviewSubmitting" @click="submitReview">{{ reviewForm.conclusion === 2 ? '确认拒绝' : '确认通过' }}</el-button>
      </div>
    </el-dialog>

    <!-- 批量拒绝弹窗 -->
    <el-dialog :visible.sync="batchRejectVisible" width="500px" :close-on-click-modal="false">
      <div slot="title"><h3 style="margin:0;">批量拒绝</h3></div>
      <div style="background:rgba(239,68,68,0.08);border:1px solid rgba(239,68,68,0.2);border-radius:6px;padding:10px 14px;font-size:12px;color:#b91c1c;margin-bottom:16px;">
        已选择 {{ selection.length }} 条待审核记录，请输入拒绝原因
      </div>
      <el-form label-width="80px" size="small">
        <el-form-item label="拒绝原因" required>
          <el-input type="textarea" v-model="batchRejectReason" rows="3" placeholder="请输入拒绝原因"></el-input>
        </el-form-item>
        <el-form-item label="审核备注">
          <el-input type="textarea" v-model="batchRejectRemark" rows="3" placeholder="请输入审核备注（选填）"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer"><el-button @click="batchRejectVisible=false">取消</el-button><el-button v-auth="'business:customerAuditRecord:review'" type="danger" @click="confirmBatchReject">确定拒绝</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import { auditRecordList, auditRecordStats, auditRecordDetail, auditRecordApprove, auditRecordReject, auditRecordBatchApprove, auditRecordBatchReject, auditRecordExport } from '@/api/customerAudit'
import { countryList } from '@/api/regConfig'

export default {
  name: 'CustomerAuditList',
  data() {
    return {
      stats: {}, tableData: [], loading: false, total: 0,
      pageNum: 1, pageSize: 15, selection: [],
      activeTab: 'all',
      search: { orderNo: '', customerNo: '', name: '', countryCode: '', authType: null, status: null, submitDate: null },
      countries: [],
      reviewVisible: false, detail: null, detailSnapshots: [],
      // 审核结论：1=通过 2=拒绝；拒绝原因只在结论为拒绝时校验
      reviewForm: { conclusion: 1, remark: '', reason: '' },
      reviewSubmitting: false,
      batchRejectVisible: false, batchRejectReason: '', batchRejectRemark: '',
      rejectReasons: [
        { value: 'blurry', label: '照片模糊/无法辨认' },
        { value: 'expired', label: '证件已过期' },
        { value: 'mismatch', label: '信息与注册信息不一致' },
        { value: 'not_owner', label: '疑似非本人' },
        { value: 'incomplete', label: '资料不完整' },
        { value: 'other', label: '其他' }
      ]
    }
  },
  computed: {
    tabTitle() {
      const map = { all: '全部审核记录', pending: '待审核列表', passed: '已通过列表', rejected: '已拒绝列表' }
      return map[this.activeTab] || '审核记录'
    }
  },
  created() { this.fetchStats(); this.fetchList(); this.fetchCountries() },
  methods: {
    // 列表/统计/导出共用的查询条件（提交时间区间转成后端要的 createDateBegin/End）
    buildParams(extra = {}) {
      const params = {}
      Object.keys(this.search).forEach(k => { if (this.search[k] !== '' && this.search[k] !== null) params[k] = this.search[k] })
      if (params.submitDate && params.submitDate.length) {
        params.createDateBegin = params.submitDate[0] + ' 00:00:00'
        params.createDateEnd = params.submitDate[1] + ' 23:59:59'
      }
      delete params.submitDate
      return Object.assign(params, extra)
    },
    fetchList() {
      this.loading = true
      const params = this.buildParams({ pageNum: this.pageNum, pageSize: this.pageSize })
      if (this.activeTab === 'pending') params.status = 1
      else if (this.activeTab === 'passed') params.status = 2
      else if (this.activeTab === 'rejected') params.status = 3
      auditRecordList(params).then(res => {
        if (res.code === 0) { this.tableData = res.result.list || []; this.total = res.result.total || 0 }
      }).finally(() => { this.loading = false })
    },
    // 统计随筛选条件变化；status 由后端忽略（统计的就是各状态分布），故这里无需剔除
    fetchStats() { auditRecordStats(this.buildParams()).then(res => { if (res.code === 0) this.stats = res.result || {} }) },
    fetchCountries() { countryList({ pageNum: 1, pageSize: 500 }).then(res => { if (res.code === 0) this.countries = res.result.list || [] }) },
    handleSearch() { this.pageNum = 1; this.fetchList(); this.fetchStats() },
    resetSearch() {
      this.search = { orderNo: '', customerNo: '', name: '', countryCode: '', authType: null, status: null, submitDate: null }
      this.activeTab = 'all'
      this.pageNum = 1
      this.fetchList(); this.fetchStats()
    },
    switchTab(tab) { this.activeTab = tab; this.pageNum = 1; this.fetchList() },
    handleSelectionChange(v) { this.selection = v },
    authTypeLabel(t) { const m = { 1: '证件信息', 2: '银行卡信息', 3: '电子钱包信息', 4: '资金来源信息' }; return m[t] || '--' },
    statusLabel(s) { const m = { 1: '待审核', 2: '已通过', 3: '已拒绝' }; return m[s] || '--' },
    statusType(s) { const m = { 1: 'warning', 2: 'success', 3: 'danger' }; return m[s] || 'info' },
    countryLabel(code) { const c = this.countries.find(x => x.countryCode === code); return c ? (c.countryCname || c.countryName) : (code || '--') },
    // 审核：资料详情 + 审核结论合并成一个弹窗（待审核记录才会出现结论区）
    openReview(row) {
      auditRecordDetail(row.id).then(res => {
        if (res.code === 0) {
          this.detail = res.result
          this.detailSnapshots = []
          if (this.detail.changeSnapshot) {
            try { this.detailSnapshots = [JSON.parse(this.detail.changeSnapshot)] } catch (e) {}
          }
          this.reviewForm = { conclusion: 1, remark: '', reason: '' }
          this.reviewSubmitting = false
          this.reviewVisible = true
        }
      })
    },
    // 变更快照里这几个字段存的是图片地址（field 名与后端 buildSubmitSnapshot 一致）
    isImageChange(ch) {
      return ['frontImageUrl', 'backImageUrl', 'handheldImageUrl', 'proofImageUrl'].indexOf(ch.field) !== -1
    },
    // 提交审核：通过/拒绝走各自的接口，审核备注两边共用
    submitReview() {
      if (this.reviewSubmitting || !this.detail) return
      const f = this.reviewForm
      const isReject = f.conclusion === 2
      if (isReject && (!f.reason || !f.reason.trim())) { this.$message.warning('请输入拒绝原因'); return }
      this.reviewSubmitting = true
      const req = isReject
        ? auditRecordReject({ id: this.detail.id, reason: f.reason, remark: f.remark })
        : auditRecordApprove({ id: this.detail.id, remark: f.remark })
      req.then(res => {
        if (res.code === 0) {
          this.$message.success(isReject ? '已拒绝' : '已通过')
          this.reviewVisible = false
          this.fetchList(); this.fetchStats()
        } else this.$message.error(res.message || '操作失败')
      }).finally(() => { this.reviewSubmitting = false })
    },
    // Batch approve
    batchApprove() {
      const pending = this.selection.filter(r => r.status === 1)
      if (!pending.length) { this.$message.warning('请勾选待审核的记录'); return }
      this.$confirm('确定批量通过 ' + pending.length + ' 条审核吗？', '批量通过', { type: 'success' }).then(() => {
        auditRecordBatchApprove(pending.map(r => r.id).join(',')).then(res => {
          if (res.code === 0) { this.$message.success('已批量通过'); this.fetchList(); this.fetchStats() }
          else this.$message.error(res.message || '操作失败')
        })
      }).catch(() => {})
    },
    // Batch reject
    openBatchReject() {
      const pending = this.selection.filter(r => r.status === 1)
      if (!pending.length) { this.$message.warning('请勾选待审核的记录'); return }
      this.batchRejectReason = ''
      this.batchRejectRemark = ''
      this.batchRejectVisible = true
    },
    confirmBatchReject() {
      if (!this.batchRejectReason || !this.batchRejectReason.trim()) { this.$message.warning('请输入拒绝原因'); return }
      const pending = this.selection.filter(r => r.status === 1)
      auditRecordBatchReject({ ids: pending.map(r => r.id).join(','), reason: this.batchRejectReason, remark: this.batchRejectRemark }).then(res => {
        if (res.code === 0) { this.$message.success('已批量拒绝'); this.batchRejectVisible = false; this.fetchList(); this.fetchStats() }
        else this.$message.error(res.message || '操作失败')
      })
    },
    // Export
    handleExport() {
      const params = this.buildParams()
      if (this.selection && this.selection.length > 0) params.ids = this.selection.map(r => r.id).join(',')
      auditRecordExport(params).then(res => {
        const blob = res instanceof Blob ? res : new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = '资料审核记录.xlsx'; a.click()
        window.URL.revokeObjectURL(url)
      })
    }
  }
}
</script>

<style scoped>
.page-container { background: #f4f6fb; min-height: calc(100vh - 84px); padding: 16px; }
.stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 16px; }
.stat-card { background: #fff; border-radius: 10px; padding: 16px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.04),0 4px 16px rgba(0,0,0,0.06); }
.stat-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.stat-label { font-size: 13px; color: #5a6578; font-weight: 500; }
.stat-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
.stat-icon.green { background: rgba(22,163,74,0.08); } .stat-icon.blue { background: rgba(37,99,235,0.08); } .stat-icon.amber { background: rgba(245,158,11,0.08); }
.stat-value { font-size: 26px; font-weight: 700; color: #1a2236; margin-bottom: 4px; font-family: 'JetBrains Mono','DM Sans',sans-serif; }
.stat-change { font-size: 12px; color: #9ca3af; }
.filter-bar { background: #fff; border-radius: 10px; padding: 14px 16px 4px; margin-bottom: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.tabs-bar { display: flex; gap: 8px; margin-bottom: 14px; }
.tab-btn { padding: 8px 20px; border: 1px solid #d1d9e6; border-radius: 6px; background: #fff; font-size: 13px; color: #5a6578; cursor: pointer; transition: all 0.15s; }
.tab-btn:hover { background: #f0f2f5; color: #1a2236; }
.tab-btn.active { background: var(--brand-primary); color: #fff; border-color: var(--brand-primary); }
.table-wrapper { background: #fff; border-radius: 10px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.04),0 4px 16px rgba(0,0,0,0.06); }
.table-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.table-title { font-size: 15px; font-weight: 600; color: #1a2236; }
.table-actions { display: flex; gap: 8px; align-items: center; }
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; font-family: inherit; cursor: pointer; transition: all 0.15s; border: 1px solid transparent; white-space: nowrap; }
.btn-primary { background: var(--brand-primary); color: #fff; border-color: var(--brand-primary); }
.btn-danger { background: #f56c6c; color: #fff; border-color: #f56c6c; }
.btn-secondary { background: transparent; color: #5a6578; border-color: #e8ecf2; }
.btn-sm { padding: 5px 10px; font-size: 12px; }
/* 弹窗分区标题（用户信息 / 证件信息…）：与 clientAccount、creditAdjustment 等页同一套写法 */
.detail-section-title { font-size: 14px; font-weight: 600; color: #1a2236; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #e8ecf2; }
.detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.detail-item { display: flex; flex-direction: column; gap: 4px; }
.di-label { font-size: 12px; color: #5a6578; }
.di-value { font-size: 14px; color: #1a2236; font-weight: 500; }
.mono { font-family: 'JetBrains Mono', monospace; }

/* Diff table — 修改前后对比 */
.diff-table { width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #e8ecf2; border-radius: 8px; overflow: hidden; }
.diff-table th { text-align: left; padding: 9px 14px; background: #f4f6fb; color: #5a6578; font-weight: 500; font-size: 12px; border-bottom: 2px solid #e8ecf2; }
.diff-table td { padding: 10px 14px; border-bottom: 1px solid #f0f2f5; vertical-align: middle; }
.diff-table tbody tr:last-child td { border-bottom: none; }
.diff-field { font-weight: 600; color: #1a2236; white-space: nowrap; }
.diff-arrow { text-align: center; font-size: 15px; font-weight: 700; color: var(--brand-primary); }
.diff-old { background: rgba(239,68,68,0.06); }
.diff-old span { color: #6b7280; text-decoration: line-through; text-decoration-color: rgba(220,38,38,0.3); }
.diff-new { background: rgba(22,163,74,0.06); }
.diff-new span { color: #16a34a; font-weight: 600; }
.diff-empty { text-decoration: none !important; color: #c0c4cc !important; font-weight: 400 !important; font-style: italic; }
/* 变更详情里的图片字段：渲染缩略图，点击用 el-image 自带预览放大 */
.diff-img { display: block; width: 96px; height: 64px; border-radius: 6px; border: 1px solid #e8ecf2; background: #fff; }
.diff-img-err { display: flex; align-items: center; justify-content: center; height: 100%; font-size: 12px; color: #c0c4cc; }

/* 审核结论区（弹窗下半部，仅待审核记录出现） */
.review-block { margin-top: 20px; }
.review-block-title { font-size: 13px; font-weight: 600; color: #303133; padding-bottom: 10px; border-bottom: 1px solid #ebeef5; margin-bottom: 16px; }
.review-radio { margin-bottom: 16px; }
.review-field { font-size: 13px; color: #5a6578; margin-bottom: 8px; }
.review-required { color: #f56c6c; }
/* 输入框走浅灰底、聚焦转白，与设计稿一致；textarea 是 el-input 内部渲染的，必须 ::v-deep 才选得到 */
.review-block ::v-deep .el-textarea__inner { background: #f4f6fb; border-color: #e8ecf2; font-size: 13px; color: #1a2236; }
.review-block ::v-deep .el-textarea__inner:focus { background: #fff; border-color: var(--brand-primary); }

/* 审核结果区（弹窗底部，已通过/已拒绝的详情回显，只读） */
.result-block { margin-top: 20px; }
.result-title { font-size: 15px; font-weight: 700; color: #1a2236; padding-bottom: 12px; border-bottom: 1px solid #ebeef5; margin-bottom: 18px; }
.result-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.result-cell { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.result-label { font-size: 12px; color: #9ca3af; }
.result-value { font-size: 14px; color: #1a2236; font-weight: 500; }
.result-pill { display: inline-flex; align-items: center; width: fit-content; padding: 3px 14px; border-radius: 999px; font-size: 13px; font-weight: 500; }
.result-pill.reject { background: rgba(239,68,68,0.1); color: #dc2626; }
.result-pill.pass { background: rgba(22,163,74,0.1); color: #16a34a; }
/* 审核备注：与拒绝原因同款「标签 + 整块内容」，但走中性浅灰底（备注不是驳回语义，不能用红） */
.result-note { margin-top: 20px; }
.result-note-label { font-size: 14px; color: #303133; margin-bottom: 10px; }
.result-note-box { background: #f4f6fb; border-radius: 6px; padding: 12px 14px; font-size: 13px; color: #5a6578; line-height: 1.6; word-break: break-word; }
/* 拒绝原因是整块浅红底，颜色比上面的小标签更重，与设计稿一致 */
.result-reason { margin-top: 20px; }
.result-reason-label { font-size: 14px; color: #303133; margin-bottom: 10px; }
.result-reason-box { background: rgba(239,68,68,0.07); border-radius: 6px; padding: 12px 14px; font-size: 13px; color: #b91c1c; line-height: 1.6; word-break: break-word; }

/* 窄屏：审核结果的三列退成两列/一列，拒绝原因整块跟着走 */
@media (max-width: 1200px) {
  .detail-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 900px) {
  .result-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .detail-grid { grid-template-columns: minmax(0, 1fr); }
}
</style>
